import _ from 'lodash';
import format from 'date-fns/format';
//services
import { getQuote, validateStep3 } from 'services/convert.services';
//reducers
import { selectTranslations } from 'state/reducers/translation.reducers';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
//components
import { FormsStack } from 'theme/elements/Forms';
import { InfoCard, LightGreenCard } from 'theme/elements/Cards';
import { FormHeader1, FormHeadWithCheckbox } from 'theme/elements/Headers';
import { Paragraph3, Paragraph5, Paragraph5withBorder } from 'theme/elements/Paragraphs';
import WarningDialog from 'components/WarningDialog';
//sections
import Footer from '../Footer';
//mui
import { Checkbox, Stack, Typography, useTheme } from '@mui/material';
//constants
import { Fragment, useMemo, useState } from 'react';
import { timing, userMessage } from 'constants/ui.constants';
import { CustomCheckbox } from 'theme/elements/Inputs';
import { Convert, ConvertData, convertSteps } from 'constants/convert.constants';
import {
	getCurrentConversionData,
	getMessage,
	setMessage,
	setConversionData,
	setActiveStep,
	isLoading,
	getNewConvert,
	getExpried,
	setExpired,
} from 'state/reducers/convert.reducers';
import { getCurrencies } from 'state/reducers/global.reducers';
import { isValid } from 'date-fns';
import Countdown from 'react-countdown';
import { getUserDetails } from 'state/reducers/user.reducers';
import { User } from 'constants/user.constants';

export default function Step2(props: { handleCancel: any }) {
	const convert: ConvertData = useAppSelector(getCurrentConversionData);
	const newConvert: Convert = useAppSelector(getNewConvert);
	const userMessage: userMessage = useAppSelector(getMessage);
	const getIsLoading = useAppSelector(isLoading);
	const user: User = useAppSelector(getUserDetails);
	const [agree, setAgree] = useState(false);
	const prefixFields = 'page.convert.create.step.2.';
	const theme = useTheme();
	const dispatch = useAppDispatch();
	const t = useAppSelector(selectTranslations);
	let CurrenciesArray = useAppSelector(getCurrencies);
	let Currencies = _.keyBy(useAppSelector(getCurrencies), function (o) {
		return o.code;
	});
	const expired = useAppSelector(getExpried);
	function getSymbol(code: string): string {
		return _.has(Currencies, code) ? Currencies[code].symbol : code;
	}
	const renderer = ({ hours, minutes, seconds, completed }) => {
		if (completed) {
			// Render a completed state
			return <span>{t(`${prefixFields}time_expired.text`)}</span>;
		} else {
			// Render a countdown
			return (
				<div>
					<div style={{ width: '25px', float: 'left' }}>{seconds}</div>
					{t(`${prefixFields}remaining_seconds.text`)}
				</div>
			);
		}
	};

	const openDialog = useMemo(() => {
		return userMessage.exist;
	}, [userMessage]);

	const counter = useMemo(() => {
		return expired ? (
			<span>{t(`${prefixFields}time_expired.text`)}</span>
		) : (
			<Countdown
				onComplete={() => {
					dispatch(setExpired(true));
				}}
				renderer={renderer}
				date={Date.now() + 20000}
			/>
		);
	}, [expired]);
	return (
		<Fragment>
			<WarningDialog
				open={openDialog}
				title={t(userMessage.text || '')}
				subTitle={userMessage.subText}
				handleCancel={() => {
					dispatch(setMessage({ exist: false }));
				}}
			/>
			<FormsStack>
				<InfoCard>
					<FormHeader1 title={t(`${prefixFields}quote.title`)} text={t(`${prefixFields}quote.text`)} />
					<Stack spacing={2}>
						<Stack direction="row" spacing={2}>
							<Paragraph5withBorder
								title={t(prefixFields + 'sell_amount.text')}
								text={
									String(convert['altshulerSellAmount']?.toLocaleString()) +
									' ' +
									convert.clientSellCurrency
								}
								symbol={getSymbol(convert.clientSellCurrency || '')}
								sx={{ flexGrow: 1, width: '50%' }}
							/>
							<Paragraph5withBorder
								title={t(prefixFields + 'buy_amount.text')}
								text={
									String(convert['altshulerBuyAmount']?.toLocaleString()) +
									' ' +
									convert.clientBuyCurrency
								}
								symbol={getSymbol(convert.clientBuyCurrency || '')}
								sx={{ flexGrow: 1 }}
							/>
						</Stack>
						<LightGreenCard>
							<Stack direction="row" justifyContent="space-between">
								<Typography>{t(prefixFields + 'client_rate.text')}</Typography>
								<Stack direction="row" spacing={1}>
									<Typography variant="boldBody1">{String(convert['altshulerRate'])}</Typography>
									<Typography variant="boldBody1">
										{t(prefixFields + 'inverse_client_rate.text')}
									</Typography>
									<Typography>{String((1 / convert.altshulerRate!).toFixed(3))}</Typography>
								</Stack>
							</Stack>
						</LightGreenCard>
						<Stack direction="row" spacing={2}>
							<Paragraph5
								title={t(prefixFields + 'conversion_date.text')}
								text={
									isValid(new Date(convert?.conversion_date || ''))
										? format(new Date(), timing.DATE)
										: convert.conversion_date
								}
								sx={{ flexGrow: 1 }}
							/>
							<Paragraph5
								title={t(prefixFields + 'settlement_date.text')}
								text={
									isValid(new Date(convert?.dueDate || ''))
										? format(new Date(convert?.dueDate || ''), timing.DATE)
										: convert.dueDate
								}
								sx={{ flexGrow: 1, width: '50%' }}
							/>
						</Stack>
					</Stack>
				</InfoCard>
				<InfoCard>
					<FormHeadWithCheckbox>
						<Stack direction="row" spacing={2} sx={{ width: '80%', wordBreak: 'break-word' }}>
							<CustomCheckbox>
								<Checkbox
									disabled={expired}
									sx={{ padding: 0, alignItems: 'start' }}
									onChange={(event) => {
										setAgree(event.target.checked);
										let n = _.clone(convert);
										n.termAgreement = event.target.checked;
										dispatch(setConversionData(n));
									}}
									checked={agree}
								/>
							</CustomCheckbox>
							<Paragraph3 title={t(prefixFields + 'disclaimer.title')} />
						</Stack>
						<Stack>
							<Typography variant="captionRegular">{t(`${prefixFields}quote_expires.text`)}</Typography>
							<Typography variant="boldBody1">{counter}</Typography>
						</Stack>
					</FormHeadWithCheckbox>
				</InfoCard>
				<Footer
					handleCancel={props.handleCancel}
					handleBack={() => {
						dispatch(setActiveStep(convertSteps.DETAILS));
					}}
					ifBack={true}
					disableSubmit={(!agree && !expired) || getIsLoading}
					labelSubmit={
						expired
							? t(`${prefixFields}requote_button.text`)
							: t(`${prefixFields}create_conversion_button.text`)
					}
					handleSubmit={() => {
						if (expired) {
							let a: Convert = _.clone(newConvert);
							a.companyId = user.companyId;
							setAgree(false);
							dispatch(getQuote(a, CurrenciesArray));
						} else {
							dispatch(validateStep3(convert));
						}
					}}
					labelCancel={t(`${prefixFields}cancel_button.text`)}
					labelBack={t(`${prefixFields}back_button.text`)}
				/>
			</FormsStack>
		</Fragment>
	);
}
